import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { rupiah, dmy } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Boxes, Trash2 } from "lucide-react";

const KINDS = [["printing", "Printing"], ["packaging", "Packaging"], ["overhead", "Overhead Produksi"]];

export default function OptionalCosts() {
  const { can } = useAuth();
  const [tab, setTab] = useState("printing");
  const [data, setData] = useState({});
  const [open, setOpen] = useState(false);
  const load = (kind) => api.get(`/costing/components/${kind}`).then((r) => setData((p) => ({ ...p, [kind]: r.data })));
  useEffect(() => { KINDS.forEach(([k]) => load(k)); }, []);
  const del = async (kind, id) => { try { await api.delete(`/costing/components/${kind}/${id}`); toast.success("Dihapus"); load(kind); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader title="Komponen HPP Full" subtitle="Printing, Packaging, Overhead Produksi — opsional. HPP Full hanya lengkap bila ketiganya tersedia." testId="components-header">
        {can("costing.manage") && <Button onClick={() => setOpen(true)} className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-component"><Plus className="mr-1.5 h-4 w-4" />Tambah {KINDS.find(([k]) => k === tab)[1]}</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="components-tabs">{KINDS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        {KINDS.map(([k]) => (
          <TabsContent key={k} value={k}>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
              {!data[k] ? <Loading /> : data[k].length === 0 ? <EmptyState title="Belum tersedia" subtitle="Komponen opsional; isi bila SPH memakainya." icon={Boxes} /> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Scope</TableHead><TableHead>Bahan</TableHead>{k === "overhead" && <TableHead>Metode</TableHead>}<TableHead className="text-right">Biaya/Pcs</TableHead><TableHead>Berlaku</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{data[k].map((r) => (
                    <TableRow key={r.id} data-testid={`comp-row-${r.id}`}>
                      <TableCell className="text-xs">{r.scope}</TableCell><TableCell className="text-xs">{r.fabric_name || "-"}</TableCell>
                      {k === "overhead" && <TableCell className="text-xs">{r.method || "-"}</TableCell>}
                      <TableCell className="text-right font-medium">{rupiah(r.cost_per_pcs)}</TableCell><TableCell className="text-xs text-slate-500">{dmy(r.effective_from)}</TableCell>
                      <TableCell className="text-right">{can("costing.manage") && <Button size="sm" variant="ghost" className="text-rose-500 h-7" onClick={() => del(k, r.id)}><Trash2 className="h-4 w-4" /></Button>}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
      {open && <ComponentDialog kind={tab} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(tab); }} />}
    </div>
  );
}

function ComponentDialog({ kind, onClose, onSaved }) {
  const [f, setF] = useState({ scope: "GLOBAL", cost_per_pcs: "", method: "MANUAL", monthly_amount: "", output_basis: "", effective_from: wibToday(), notes: "" });
  const save = async () => {
    const body = { scope: f.scope, effective_from: f.effective_from, status: "active", notes: f.notes, cost_per_pcs: Number(f.cost_per_pcs || 0) };
    if (kind === "overhead") { body.method = f.method; if (f.method === "MONTHLY_DIV_OUTPUT") { body.monthly_amount = Number(f.monthly_amount); body.output_basis = Number(f.output_basis); } }
    try { await api.post(`/costing/components/${kind}`, body); toast.success("Tersimpan"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="component-dialog">
      <DialogHeader><DialogTitle className="font-display">Tambah Biaya {kind}</DialogTitle><DialogDescription>Komponen opsional HPP Full. Jangan mengarang biaya.</DialogDescription></DialogHeader>
      {kind === "overhead" && (
        <div><Label className="text-xs text-slate-500">Metode Alokasi</Label><Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger className="mt-1" data-testid="oh-method"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="FIXED_PER_PCS">Biaya Tetap / Pcs</SelectItem><SelectItem value="MONTHLY_DIV_OUTPUT">Overhead Bulanan ÷ Output</SelectItem><SelectItem value="MANUAL">Manual / Pcs</SelectItem></SelectContent></Select></div>
      )}
      {kind === "overhead" && f.method === "MONTHLY_DIV_OUTPUT" ? (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Overhead Bulanan</Label><Input type="number" className="mt-1" value={f.monthly_amount} onChange={(e) => setF({ ...f, monthly_amount: e.target.value })} data-testid="oh-monthly" /></div>
          <div><Label className="text-xs text-slate-500">Basis Output (pcs)</Label><Input type="number" className="mt-1" value={f.output_basis} onChange={(e) => setF({ ...f, output_basis: e.target.value })} data-testid="oh-output" /></div>
        </div>
      ) : (
        <div><Label className="text-xs text-slate-500">Biaya / Pcs</Label><Input type="number" className="mt-1" value={f.cost_per_pcs} onChange={(e) => setF({ ...f, cost_per_pcs: e.target.value })} data-testid="comp-cost" /></div>
      )}
      <div><Label className="text-xs text-slate-500">Berlaku Sejak</Label><Input type="date" className="mt-1" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-component">Simpan</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
